// src/components/CartDrawer.js
// Slide-out cart drawer — shows cart items, quantities, and checkout button.
// Uses CurrencyContext for the currency symbol so it stays consistent
// with the rest of the site when the user switches currency.

import React, { useState, useEffect } from 'react';
import { trackViewCart } from '../lib/analytics';
import { X, Plus, Minus, Trash2 } from 'lucide-react';
import { useCart, GIFT_BAG_VARIANT_ID, GIFT_BAG_HANDLE } from '../context/CartContext';
import { useCurrency, CURRENCIES } from '../context/CurrencyContext';
import { getScent } from '../data/scents';
import { Switch } from './ui/switch';
import CartRecommendation from './CartRecommendation';

const CartDrawer = () => {
    const {
        cart,
        isOpen,
        setIsOpen,
        addToCart,
        updateQuantity,
        removeFromCart,
        goToCheckout,
        loading,
    } = useCart();
    const [giftBagError, setGiftBagError] = useState('');
    // Price to show before the gift bag is actually in the cart — fetched
    // per-currency the same way ProductDetail.js fetches product prices,
    // since there's no cart line to read a converted price from yet.
    const [giftBagBasePrice, setGiftBagBasePrice] = useState(null);

    // Derive the symbol from the cart's own currency (as returned by Shopify)
    // rather than the site-wide selection, so the two can't briefly disagree
    // while a cart re-fetch triggered by a currency switch is still in flight.
    const { symbol: fallbackSymbol, country } = useCurrency();
    const symbol = CURRENCIES.find(c => c.code === cart?.currency)?.symbol || fallbackSymbol;

    // Only look this up while the drawer is actually open, and re-fetch if
    // the shopper switches currency while it's open — mirrors the
    // [handle, country] re-fetch pattern in ProductDetail.js.
    // view_cart once each time the drawer opens
    useEffect(() => {
        if (isOpen) trackViewCart(cart);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        getScent(GIFT_BAG_HANDLE, country)
            .then(product => {
                if (!cancelled && product) setGiftBagBasePrice(product.price);
            })
            .catch(() => {
                // Lookup failed — leave giftBagBasePrice as-is (label just
                // omits the price until it succeeds)
            });
        return () => { cancelled = true; };
    }, [isOpen, country]);

    if (!isOpen) return null;

    const lines = cart?.lines || [];
    const total = cart?.total || 0;

    // Gift bag is an add-on toggle, not an editable line item — keep it out
    // of the regular items list but derive its checked state from the real
    // cart so it reflects reality on drawer open/reopen and after refresh.
    const giftBagLine = lines.find(line => line.variantId === GIFT_BAG_VARIANT_ID);
    const hasGiftBag = Boolean(giftBagLine);
    const displayLines = lines.filter(line => line.variantId !== GIFT_BAG_VARIANT_ID);
    // Use the real line price once it's in the cart (already converted to the
    // cart's currency by Shopify); before that, use the fetched per-currency
    // price from giftBagBasePrice — null until that lookup resolves.
    const giftBagPrice = giftBagLine?.price ?? giftBagBasePrice;

    const handleGiftBagToggle = async (checked) => {
        setGiftBagError('');
        try {
            if (checked) {
                await addToCart(GIFT_BAG_VARIANT_ID, 1);
            } else if (giftBagLine) {
                await removeFromCart(giftBagLine.lineId);
            }
        } catch (err) {
            setGiftBagError(err.message || 'Could not update gift bag.');
        }
    };

    return (
        <>
            {/* Backdrop — closes drawer on click */}
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
                onClick={() => setIsOpen(false)}
            />

            <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-background border-l border-border z-50 overflow-hidden flex flex-col transition-colors duration-300">

                {/* Header */}
                <div className="p-6 pt-[50px] border-b border-border flex items-center justify-between">
                    <h2 className="text-xl font-light text-[#CEAC8F] tracking-wide">Your Cart</h2>
                    <button
                        onClick={() => setIsOpen(false)}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Cart items */}
                <div className="flex-1 overflow-y-auto p-6">
                    {lines.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-muted-foreground text-sm">Your cart is empty.</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {displayLines.map((item) => (
                                <div key={item.lineId} className="flex gap-4 pb-6 border-b border-border">
                                    <div className="w-20 h-20 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                                        <img
                                            src={item.image}
                                            alt={item.imageAlt}
                                            className="w-full h-full object-contain"
                                        />
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <h3 className="text-foreground font-medium text-sm">{item.title}</h3>
                                                <p className="text-muted-foreground text-xs mt-1">12ml</p>
                                                {item.variantTitle !== 'Default Title' && (
                                                    <p className="text-muted-foreground text-xs mt-1">{item.variantTitle}</p>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => removeFromCart(item.lineId)}
                                                disabled={loading}
                                                className="text-muted-foreground hover:text-red-400 transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>

                                        <div className="flex items-center justify-between mt-3">
                                            {/* Quantity controls */}
                                            <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
                                                <button
                                                    onClick={() => updateQuantity(item.lineId, item.quantity - 1)}
                                                    disabled={loading || item.quantity <= 1}
                                                    className="text-muted-foreground hover:text-[#CEAC8F] transition-colors p-1 disabled:opacity-40"
                                                >
                                                    <Minus className="w-3 h-3" />
                                                </button>
                                                <span className="text-foreground text-sm w-8 text-center">{item.quantity}</span>
                                                <button
                                                    onClick={() => updateQuantity(item.lineId, item.quantity + 1)}
                                                    disabled={loading}
                                                    className="text-muted-foreground hover:text-[#CEAC8F] transition-colors p-1 disabled:opacity-40"
                                                >
                                                    <Plus className="w-3 h-3" />
                                                </button>
                                            </div>

                                            {/* Line item price */}
                                            <p className="text-[#CEAC8F] font-medium text-sm">
                                                {symbol}{(item.price * item.quantity).toFixed(2)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {/* One personalised suggestion, below the items */}
                            <CartRecommendation lines={displayLines} country={country} symbol={symbol} />
                        </div>
                    )}
                </div>

                {/* Footer — subtotal and checkout */}
                {lines.length > 0 && (
                    <div className="border-t border-border p-6 space-y-4">
                        {/* Gift bag add-on */}
                        <div>
                            <div className="flex items-center justify-between">
                                <span className="text-foreground text-sm">
                                    Add a gift bag 🎁{giftBagPrice != null && ` — ${symbol}${giftBagPrice.toFixed(2)}`}
                                </span>
                                <Switch
                                    checked={hasGiftBag}
                                    disabled={loading}
                                    onCheckedChange={handleGiftBagToggle}
                                    aria-label="Add a gift bag"
                                    className="data-[state=checked]:bg-[#CEAC8F]"
                                />
                            </div>
                            {giftBagError && (
                                <p className="text-red-400 text-xs mt-1">{giftBagError}</p>
                            )}
                        </div>

                        <div className="flex justify-between items-center">
                            <span className="text-muted-foreground text-sm">Subtotal</span>
                            <span className="text-[#CEAC8F] text-xl font-light tracking-wide">
                                {symbol}{total.toFixed(2)}
                            </span>
                        </div>

                        <button
                            onClick={() => goToCheckout()}
                            disabled={loading}
                            className="w-full bg-[#CEAC8F] text-black py-4 rounded-lg font-medium hover:bg-[#d4b896] transition-colors disabled:opacity-60"
                        >
                            {loading ? 'Updating...' : 'Proceed to Checkout'}
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default CartDrawer;